import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { GooseLogo, FadeIn, SectionLabel } from "@/components/common";
import { Button } from "@/components/ui/button";

const GENDERS = [
  { v: "woman", label: "Woman" }, { v: "man", label: "Man" }, { v: "non-binary", label: "Non-binary" },
];
const AGES = ["18-24", "25-34", "35-44", "45-54", "55+"];
const INTENTS = [
  { v: "long-term", label: "Long-term" }, { v: "casual", label: "Casual" },
];

function Chip({ active, onClick, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`btn-pill border px-4 py-2.5 text-sm font-semibold ${active ? "border-rose-500 bg-rose-500/15 text-rose-300" : "border-slate-700 bg-white/5 text-slate-300 hover:border-slate-500"}`}>
      {children}
    </button>
  );
}

export default function GuestSurvey() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("e") || "";
  const [answers, setAnswers] = useState({ gender: "", age_bracket: "", intent: "" });
  const [done, setDone] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!answers.gender || !answers.age_bracket || !answers.intent) { toast.error("Answer all three"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/public/events/${slug}/survey-complete`, { email, ...answers });
      setDone(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setSaving(false); }
  };

  return (
    <div className="goose-grain min-h-screen">
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col px-4 py-8">
        <GooseLogo className="text-xl" onClick={() => navigate("/")} />

        {done ? (
          <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 16 }}
            className="goose-card relative mt-16 overflow-hidden p-8 text-center" data-testid="survey-done">
            <div className="absolute -inset-10 bg-rose-500/20 blur-3xl" />
            <div className="relative">
              <PartyPopper className="mx-auto h-8 w-8 text-amber-400" />
              <p className="mt-4 text-sm text-slate-400">Survey complete! Your Goose number:</p>
              <p className="font-mono-g mt-3 text-6xl font-black tracking-widest text-rose-500 glow-rose">#{done.goose_number}</p>
              <p className="mt-4 text-xs text-slate-500">See you on the night. You'll verify by email and look up compatibility by badge number.</p>
            </div>
          </motion.div>
        ) : (
          <>
            <FadeIn className="mt-8">
              <SectionLabel>HAEVN survey</SectionLabel>
              <h1 className="font-display mt-2 text-3xl font-bold">A few quick questions</h1>
              <p className="mt-2 text-sm text-slate-400">This stands in for the full HAEVN compatibility survey in V1.</p>
            </FadeIn>

            <FadeIn delay={0.1} className="mt-8 space-y-7">
              <div>
                <p className="mb-3 text-sm font-semibold text-slate-200">I identify as</p>
                <div className="flex flex-wrap gap-2">
                  {GENDERS.map((g) => <Chip key={g.v} testId={`gender-${g.v}`} active={answers.gender === g.v} onClick={() => setAnswers((a) => ({ ...a, gender: g.v }))}>{g.label}</Chip>)}
                </div>
              </div>
              <div>
                <p className="mb-3 text-sm font-semibold text-slate-200">Age</p>
                <div className="flex flex-wrap gap-2">
                  {AGES.map((a) => <Chip key={a} testId={`age-${a}`} active={answers.age_bracket === a} onClick={() => setAnswers((p) => ({ ...p, age_bracket: a }))}>{a}</Chip>)}
                </div>
              </div>
              <div>
                <p className="mb-3 text-sm font-semibold text-slate-200">I'm looking for</p>
                <div className="flex flex-wrap gap-2">
                  {INTENTS.map((i) => <Chip key={i.v} testId={`intent-${i.v}`} active={answers.intent === i.v} onClick={() => setAnswers((a) => ({ ...a, intent: i.v }))}>{i.label}</Chip>)}
                </div>
              </div>
            </FadeIn>

            <div className="mt-auto pt-8">
              <Button data-testid="survey-submit-btn" disabled={saving} onClick={submit}
                className="btn-pill w-full bg-rose-500 py-6 text-base font-bold hover:bg-rose-400">
                {saving ? "Submitting…" : "Submit survey"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
